import { Button } from "dash-styled"
import React, { FC, useCallback, useEffect, useMemo } from "react"
import { OrgDashboardBase } from "../DashboardBase/OrgDashboardBase"
import { Table } from "resha"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { SortChooserProps } from "../../../../Common/Button/SortChooser"
import { useDispatch } from "react-redux"
import { getValue } from "../../../../../Models/Client/FetchState"
import {
    getWorkerGroupId,
    APIWorkerGroup
} from "../../../../../Models/API/WorkerGroup"
import { useWorkerGroupList } from "../../../../../Hooks/useWorkerGroupList"
import { WorkerSubMenu } from "./Common/WorkerSubMenu"
import {
    defaultWorkerSort,
    workerGroupColumns,
    workerGroupSortFields
} from "./Common/constants"
import { WorkerGroupAddModal } from "./Common/WorkerGroupAddModal"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import {
    createWorkerGroup,
    removeWorkerGroup,
    updateWorkerGroup
} from "../../../../../Actions/API/WorkerGroupActions"
import { MainThunkDispatch } from "../../../../../Reducers"
import { Confirm } from "../../../../Common/Modal/Confirm"
import { WorkerGroupModifyModal } from "./Common/WorkerGroupModifyModal"
import { useWorkerList } from "../../../../../Hooks/useWorkerList"
import { push, replace } from "connected-react-router"
import { routePaths } from "../../../../../Routes"
import { useModal } from "../../../../../Hooks/useModal"

type Modal =
    | { type: "add" }
    | { type: "modify-workers"; group: APIWorkerGroup }
    | { type: "remove"; group: APIWorkerGroup }

const rowActions = ["Modify workers", "Delete group"] as const

export const WorkerGroupList: FC<{
    orgUsername: string
    showAddModal: boolean
    sort: SortExpression<APIWorkerGroup>
}> = ({ orgUsername, showAddModal, sort }) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const [groupsFetch, loadMoreGroups] = useWorkerGroupList(orgUsername, sort)
    const [workersFetch, loadMoreWorkers] = useWorkerList(
        orgUsername,
        defaultWorkerSort
    )
    const { modal, isSaving, open, close, save } = useModal<Modal>(
        showAddModal ? { type: "add" } : undefined
    )
    const sortOptions = useMemo<SortChooserProps<APIWorkerGroup>>(
        () => ({
            fields: workerGroupSortFields,
            applied: sort,
            onChange: newSort =>
                dispatch(
                    push(routePaths.orgWorkerGroupList(orgUsername, newSort))
                )
        }),
        [dispatch, orgUsername, sort]
    )
    const handleAddGroup = useCallback(
        (name: string) => {
            if (modal?.type === "add") {
                save(createWorkerGroup(orgUsername, name))
            }
        },
        [modal, orgUsername, save]
    )
    const handleModifyGroup = useCallback(
        (group: APIWorkerGroup) => {
            if (modal?.type === "modify-workers") {
                save(updateWorkerGroup(orgUsername, group))
            }
        },
        [modal, orgUsername, save]
    )
    const itemCount = getValue(groupsFetch)?.total
    const handleRowAction = useCallback(
        (selected: typeof rowActions[number], id: number) => {
            const group = getValue(groupsFetch)?.items.find(
                group => group.id === id
            )

            if (group === undefined) {
                return
            }

            switch (selected) {
                case "Modify workers":
                    return open({
                        type: "modify-workers",
                        group
                    })
                case "Delete group":
                    return open({
                        type: "remove",
                        group
                    })
            }
        },
        [groupsFetch, open]
    )
    const columns = useMemo(
        () => workerGroupColumns([...rowActions], handleRowAction),
        [handleRowAction]
    )
    const handleDeleteGroup = useCallback(() => {
        if (modal?.type === "remove") {
            save(removeWorkerGroup(orgUsername, modal.group.id))
        }
    }, [modal, orgUsername, save])
    const props = {
        orgUsername,
        title: "Worker Groups",
        description: "Organized group of workers",
        fixedContentWidth: false
    }

    useEffect(() => {
        if (modal === undefined) {
            dispatch(replace(routePaths.orgWorkerGroupList(orgUsername)))
        }
    }, [dispatch, modal, orgUsername])

    return (
        <OrgDashboardBase {...props}>
            <div className="flex-grow h-0 flex-basis-0 flex items-start overflow-hidden -mb-6 -mr-6">
                <WorkerSubMenu
                    activeMenu="group"
                    className="mr-6 mt-3"
                    orgUsername={orgUsername}
                />
                <div className="flex-grow h-full flex flex-col">
                    <TableHeader
                        className="mr-6"
                        filteredCount={itemCount}
                        totalCount={itemCount}
                        sortOptions={sortOptions}
                        extraActions={
                            <Button
                                size="xs"
                                variant="contained2"
                                color="darkviolet"
                                className="px-4 ml-4"
                                onClick={() => open({ type: "add" })}
                            >
                                Add worker group
                            </Button>
                        }
                    />
                    <div className="flex-grow h-0 flex-basis-0 overflow-auto pr-6">
                        <Table
                            className="w-full"
                            columns={columns}
                            getKey={getWorkerGroupId}
                            items={getValue(groupsFetch)?.items}
                            itemCount={itemCount}
                            stickyHeader={true}
                            loadMore={loadMoreGroups}
                        />
                    </div>
                </div>
            </div>
            {modal?.type === "add" && (
                <WorkerGroupAddModal
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleAddGroup}
                />
            )}
            {modal?.type === "modify-workers" && (
                <WorkerGroupModifyModal
                    group={modal.group}
                    workers={getValue(workersFetch)?.items ?? []}
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleModifyGroup}
                    onLoadMoreWorkers={loadMoreWorkers}
                />
            )}
            {modal?.type === "remove" && (
                <Confirm
                    title={`Remove ${modal.group.name}?`}
                    yesLabel={isSaving ? "Removing..." : "Remove"}
                    noLabel="Cancel"
                    isProcessing={isSaving}
                    onClose={close}
                    onClickYes={handleDeleteGroup}
                    onClickNo={close}
                />
            )}
        </OrgDashboardBase>
    )
}
